import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LAPModule } from '../types';
import {
  Award,
  Clock,
  FileCheck,
  AlertCircle,
  CheckCircle,
  Play,
  Info,
} from 'lucide-react';

export default function LAPSimulation() {
  const navigate = useNavigate();
  const [modules, setModules] = useState<LAPModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<LAPModule | null>(null);
  const [readinessScores, setReadinessScores] = useState<Map<string, number>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLAPModules();
  }, []);

  const fetchLAPModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const [modulesRes, readinessRes] = await Promise.all([
        fetch('/api/lap/modules', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/lap/readiness', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (modulesRes.ok) {
        const data = await modulesRes.json();
        setModules(data.modules);
      }

      if (readinessRes.ok) {
        const data = await readinessRes.json();
        const scoresMap = new Map(
          Object.entries(data.readiness) as [string, number][]
        );
        setReadinessScores(scoresMap);
      }
    } catch (error) {
      console.error('Failed to fetch LAP modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSimulation = async (moduleId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/lap/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ moduleId }),
      });

      if (!response.ok) throw new Error('Failed to start simulation');

      const data = await response.json();
      navigate(`/test/lap?attemptId=${data.attemptId}`);
    } catch (error) {
      console.error('Failed to start simulation:', error);
      alert('Fehler beim Starten der Simulation');
    }
  };

  const getReadinessColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getReadinessIcon = (score: number) => {
    if (score >= 80) return CheckCircle;
    if (score >= 60) return Clock;
    return AlertCircle;
  };

  const getReadinessText = (score: number) => {
    if (score >= 80) return 'Bereit für LAP';
    if (score >= 60) return 'Noch üben';
    return 'Mehr Vorbereitung nötig';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-700 rounded-lg shadow p-6 text-white">
        <div className="flex items-center">
          <Award className="h-12 w-12 mr-4" />
          <div>
            <h1 className="text-3xl font-bold">
              Lehrabschlussprüfung Simulation
            </h1>
            <p className="mt-2 text-primary-100">
              Bereite dich optimal auf die LAP gemäß BGBl. II Nr. 222/2018 vor
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <Info className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Über die LAP-Simulation
            </h3>
            <p className="text-blue-800 text-sm">
              Die LAP-Simulation orientiert sich exakt an der offiziellen
              Lehrabschlussprüfung. Sie umfasst theoretische und praktische
              Prüfungsteile mit realistischen Zeitlimits und
              Bewertungskriterien.
            </p>
            <ul className="mt-3 space-y-1 text-sm text-blue-800">
              <li>• Theoretische Prüfung: Datentechnik, Mathematik, Netzwerktechnik</li>
              <li>• Praktische Prüfung: Prüfarbeit und Fachgespräch</li>
              <li>• Realistische Zeitlimits und Bewertung</li>
              <li>• Detailliertes Feedback zu allen Antworten</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {modules.map((module) => {
          const readiness = readinessScores.get(module.module_code) || 0;
          const ReadinessIcon = getReadinessIcon(readiness);

          return (
            <div
              key={module.id}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <div
                className={`p-1 ${
                  module.module_type === 'theoretical'
                    ? 'bg-blue-500'
                    : 'bg-purple-500'
                }`}
              />
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <FileCheck
                        className={`h-6 w-6 mr-2 ${
                          module.module_type === 'theoretical'
                            ? 'text-blue-600'
                            : 'text-purple-600'
                        }`}
                      />
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          module.module_type === 'theoretical'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {module.module_type === 'theoretical'
                          ? 'THEORETISCH'
                          : 'PRAKTISCH'}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {module.module_name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600">
                      {module.description}
                    </p>
                  </div>
                </div>

                {/* Module Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>
                      Zeitlimit: {module.time_limit_minutes} Minuten
                    </span>
                  </div>
                  {module.module_type === 'theoretical' && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FileCheck className="h-4 w-4 mr-2" />
                      <span>
                        {module.questions_per_area} Fragen pro Bereich
                      </span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span>
                      Mindestens {module.pass_percentage}% zum Bestehen
                    </span>
                  </div>
                </div>

                {/* Areas */}
                {module.areas && Array.isArray(module.areas) && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Prüfungsbereiche:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {module.areas.map((area, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Readiness Indicator */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Deine Bereitschaft
                    </span>
                    <div className="flex items-center">
                      <ReadinessIcon
                        className={`h-5 w-5 mr-1 ${getReadinessColor(
                          readiness
                        )}`}
                      />
                      <span
                        className={`text-lg font-bold ${getReadinessColor(
                          readiness
                        )}`}
                      >
                        {readiness}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        readiness >= 80
                          ? 'bg-green-500'
                          : readiness >= 60
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${readiness}%` }}
                    />
                  </div>
                  <p
                    className={`mt-2 text-sm font-medium ${getReadinessColor(
                      readiness
                    )}`}
                  >
                    {getReadinessText(readiness)}
                  </p>
                </div>

                {/* Actions */}
                <button
                  onClick={() => setSelectedModule(module)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Simulation starten
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation Modal */}
      {selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Simulation starten?
            </h3>
            <p className="text-gray-600 mb-6">
              Du bist dabei, die LAP-Simulation für{' '}
              <strong>{selectedModule.module_name}</strong> zu starten. Die
              Simulation dauert{' '}
              <strong>{selectedModule.time_limit_minutes} Minuten</strong> und
              kann nicht pausiert werden.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-yellow-800">
                    <strong>Wichtig:</strong> Stelle sicher, dass du während
                    der gesamten Simulationszeit ungestört arbeiten kannst.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setSelectedModule(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  startSimulation(selectedModule.id);
                  setSelectedModule(null);
                }}
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Jetzt starten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}